<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card variant="tonal" class="pa-4 mb-4">
          <div class="d-flex justify-space-between align-center">
            <h1 class="text-h4 mb-0">{{ $t('courierTransfers.title') }}</h1>
            <v-btn color="primary" @click="showAddDialog = true">{{ $t('courierTransfers.add') }}</v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Filters -->
    <v-row>
      <v-col cols="12">
        <v-card variant="tonal" class="pa-4">
          <v-row>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filterStatus"
                :items="statusOptions"
                item-title="text"
                item-value="value"
                :label="$t('courierTransfers.status')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filterCoin"
                :items="coins"
                item-title="symbol"
                item-value="id"
                :label="$t('courierTransfers.coin')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filterCourier"
                :items="users"
                item-title="email"
                item-value="id"
                :label="$t('courierTransfers.courier')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filterDateFrom"
                :label="$t('courierTransfers.dateFrom')"
                type="date"
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filterDateTo"
                :label="$t('courierTransfers.dateTo')"
                type="date"
                density="compact"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card variant="outlined">
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="filteredTransfers"
              :items-per-page="10"
              :items-per-page-options="[10, 25, 50, 100]"
              class="elevation-0"
            >
              <template #item.status="{ item }">
                <v-chip :color="getStatusColor(item.status)" size="small">
                  {{ getStatusText(item.status) }}
                </v-chip>
              </template>
              <template #item.amount="{ item }">
                {{ Number(item.amount || 0).toLocaleString() }} {{ item.coin?.symbol || '' }}
              </template>
              <template #item.source_name="{ item }">
                {{ item.source_name || '-' }}
              </template>
              <template #item.destination_name="{ item }">
                {{ item.destination_name || '-' }}
              </template>
              <template #item.courier_email="{ item }">
                {{ item.courier?.email || '-' }}
              </template>
              <template #item.transaction_date="{ item }">
                {{ formatDate(item.transaction_date) }}
              </template>
              <template #item.actions="{ item }">
                <v-btn variant="plain" :title="$t('walletOperations.linkTitle')" icon="mdi-format-list-bulleted" @click="$router.push({ path: '/wallet-operations', query: { document_type: 'CourierTransfer', document_id: item.id } })"></v-btn>
                <v-btn variant="plain" @click="editTransfer(item)" icon="mdi-pencil"></v-btn>
                <v-btn variant="plain" color="error" @click="deleteTransfer(item.id)" icon="mdi-delete"></v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="showAddDialog" max-width="1000" @update:model-value="(val) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title class="ma-4">{{ transferForm.id ? $t('courierTransfers.edit') : $t('courierTransfers.add') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-row class="px-4 my-0">
              <v-col cols="12" md="6" class="py-0">
                <v-text-field v-model="transferForm.transaction_date" :label="$t('courierTransfers.transactionDate')" type="datetime-local" required />
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <v-select
                  v-model="transferForm.courier_id"
                  :items="users"
                  item-title="email"
                  item-value="id"
                  :label="$t('courierTransfers.courier')"
                />
              </v-col>
            </v-row>
            <v-row class="my-0 px-4">
              <v-col cols="12" md="6" class="py-1">
                <v-text-field 
                  v-model="formattedAmount" 
                  :label="$t('courierTransfers.amount')" 
                  type="text" 
                  density="compact"
                  required
                  class="amount-field-right"
                />
              </v-col>
              <v-col cols="12" md="6" class="py-1">
                <v-select
                  v-model="transferForm.coin_id"
                  :items="fiatCoins"
                  item-title="name"
                  item-value="id"
                  :label="$t('courierTransfers.coin')"
                  density="compact"
                  required
                />
              </v-col>
            </v-row>
            <v-row class="my-0 px-6">
              <v-col cols="6" class="py-0 mb-2">
                <div class="text-caption text-medium-emphasis mb-1">{{ $t('courierTransfers.source') }}</div>
                <v-radio-group v-model="sourceType" inline density="compact" hide-details>
                  <v-radio :label="$t('courierTransfers.fromWallet')" value="wallet" density="compact"></v-radio>
                  <v-radio :label="$t('courierTransfers.fromClient')" value="client" density="compact"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="6" class="px-6 py-0">
                <div class="text-caption text-medium-emphasis mb-1">{{ $t('courierTransfers.destination') }}</div>
                <v-radio-group v-model="destinationType" inline density="compact" hide-details>
                  <v-radio :label="$t('courierTransfers.toWallet')" value="wallet" density="compact"></v-radio>
                  <v-radio :label="$t('courierTransfers.toClient')" value="client" density="compact"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            <v-row class="my-0 px-4">
              <v-col cols="12" md="6" class="py-0">
                <v-select
                  v-if="sourceType === 'wallet'"
                  v-model="transferForm.from_wallet_id"
                  :items="wallets"
                  item-title="display_name"
                  item-value="id"
                  :label="$t('courierTransfers.selectWallet')"
                  clearable
                />
                <v-select
                  v-if="sourceType === 'client'"
                  v-model="transferForm.from_client_id"
                  :items="clients"
                  item-title="phone"
                  item-value="id"
                  :label="$t('courierTransfers.selectClient')"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <v-select
                  v-if="destinationType === 'wallet'"
                  v-model="transferForm.to_wallet_id"
                  :items="wallets"
                  item-title="display_name"
                  item-value="id"
                  :label="$t('courierTransfers.selectWallet')"
                  clearable
                />
                <v-select
                  v-if="destinationType === 'client'"
                  v-model="transferForm.to_client_id"
                  :items="clients"
                  item-title="phone"
                  item-value="id"
                  :label="$t('courierTransfers.selectClient')"
                  clearable
                />
              </v-col>
            </v-row>
            <v-row class="mt-0 mb-6 px-4">
              <v-col cols="12" md="6" class="py-0">
                <div class="text-caption text-medium-emphasis mb-1 pl-2">{{ $t('courierTransfers.status') }}</div>
                <v-btn-toggle
                  v-model="transferForm.status"
                  mandatory
                  variant="outlined"
                  density="compact"
                  class="w-100"
                >
                  <v-btn
                    v-for="option in statusOptions"
                    :key="option.value"
                    :value="option.value"
                    size="small"
                  >
                    {{ option.text }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
            <v-row class="my-0 px-4">
              <v-col cols="12" class="py-0">
                <v-textarea 
                  v-model="transferForm.description" 
                  :label="$t('courierTransfers.description')" 
                  rows="2" 
                  density="compact"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-3 pt-0">
          <v-spacer></v-spacer>
          <v-btn size="small" @click="showAddDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn size="small" color="primary" @click="saveTransfer">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

const { t } = useI18n()

interface Coin {
  id: number
  name: string
  symbol: string
  fiat: boolean
}

interface User {
  id: number
  email: string
}

interface Client {
  id: number
  phone: string
}

interface Wallet {
  id: number
  display_name: string
  coin: { symbol: string }
  exchange: { name: string } | null
  office: { name: string } | null
}

interface CourierTransfer {
  id: number
  courier: User
  coin: Coin
  amount: number
  status: string
  from_wallet_id?: number
  from_client_id?: number
  to_client_id?: number
  to_wallet_id?: number
  from_wallet?: Wallet
  from_client?: Client
  to_client?: Client
  to_wallet?: Wallet
  description?: string
  transaction_date: string
  source_name?: string
  destination_name?: string
  courier_email?: string
}

const transfers = ref<CourierTransfer[]>([])
const coins = ref<Coin[]>([])
const users = ref<User[]>([])
const clients = ref<Client[]>([])
const wallets = ref<Wallet[]>([])
const filterStatus = ref<string | null>(null)
const filterCoin = ref<number | null>(null)
const filterCourier = ref<number | null>(null)
const filterDateFrom = ref<string>('')
const filterDateTo = ref<string>('')
const sourceType = ref<'wallet' | 'client'>('wallet')
const destinationType = ref<'wallet' | 'client'>('client')
const headers = computed(() => [
  { title: t('courierTransfers.transactionDate'), key: 'transaction_date', sortable: true },
  { title: t('courierTransfers.courier'), key: 'courier_email', sortable: true },
  { title: t('courierTransfers.amount'), key: 'amount', sortable: true, align: 'end' as const },
  { title: t('courierTransfers.status'), key: 'status', sortable: true },
  { title: t('courierTransfers.source'), key: 'source_name', sortable: true },
  { title: t('courierTransfers.destination'), key: 'destination_name', sortable: true },
  { title: t('common.actions'), key: 'actions', sortable: false },
])
const showAddDialog = ref(false)
const transferForm = ref({
  id: null as number | null,
  courier_id: null as number | null,
  coin_id: null as number | null,
  amount: 0,
  status: 'received_from_wallets',
  from_wallet_id: null as number | null,
  from_client_id: null as number | null,
  to_client_id: null as number | null,
  to_wallet_id: null as number | null,
  description: '',
  transaction_date: new Date().toISOString().slice(0, 16)
})

const statusOptions = computed(() => [
  { text: t('courierTransfers.statusReceived'), value: 'received_from_wallets' },
  { text: t('courierTransfers.statusInWay'), value: 'in_a_way' },
  { text: t('courierTransfers.statusFinished'), value: 'finished' }
])

const formattedAmount = computed({
  get: () => {
    const value = transferForm.value.amount || 0
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 18
    }).format(value).replace(/,/g, ' ')
  },
  set: (value: string) => {
    // Remove all non-digit characters except decimal point (including spaces)
    const cleaned = value.replace(/[^\d.]/g, '')
    // Parse to number
    const numValue = parseFloat(cleaned) || 0
    transferForm.value.amount = numValue
  }
})

const fiatCoins = computed(() => {
  return coins.value.filter(coin => coin.fiat === true)
})

const loadCoins = async () => {
  try {
    const response = await apiGet('/api/coins')
    if (response.ok) {
      coins.value = await response.json()
    }
  } catch (error) {
    console.error('Load coins error:', error)
  }
}

const loadUsers = async () => {
  try {
    const response = await apiGet('/api/users')
    if (response.ok) {
      users.value = await response.json()
    }
  } catch (error) {
    console.error('Load users error:', error)
  }
}

const loadClients = async () => {
  try {
    const response = await apiGet('/api/clients')
    if (response.ok) {
      clients.value = await response.json()
    }
  } catch (error) {
    console.error('Load clients error:', error)
  }
}

const loadWallets = async () => {
  try {
    const response = await apiGet('/api/wallets')
    if (response.ok) {
      const data = await response.json()
      wallets.value = data.map((w: any) => ({
        id: w.id,
        display_name: w.display_name || (w.office ? `Office - ${w.coin.symbol}` : (w.exchange ? `${w.exchange.name} - ${w.coin.symbol}` : w.coin.symbol)),
        coin: w.coin,
        exchange: w.exchange,
        office: w.office
      }))
    }
  } catch (error) {
    console.error('Load wallets error:', error)
  }
}

const loadTransfers = async () => {
  try {
    let url = '/api/courier-transfers'
    const params = new URLSearchParams()
    if (filterStatus.value) params.append('status', filterStatus.value)
    if (filterCoin.value) params.append('coin_id', filterCoin.value.toString())
    if (filterCourier.value) params.append('courier_id', filterCourier.value.toString())
    if (filterDateFrom.value) params.append('date_from', filterDateFrom.value)
    if (filterDateTo.value) params.append('date_to', filterDateTo.value)
    if (params.toString()) url += '?' + params.toString()
    
    const response = await apiGet(url)
    if (response.ok) {
      transfers.value = await response.json()
    }
  } catch (error) {
    console.error('Load transfers error:', error)
  }
}

const saveTransfer = async () => {
  try {
    // Clear opposite source/destination fields based on selection
    if (sourceType.value === 'wallet') {
      transferForm.value.from_client_id = null
    } else {
      transferForm.value.from_wallet_id = null
    }
    
    if (destinationType.value === 'wallet') {
      transferForm.value.to_client_id = null
    } else {
      transferForm.value.to_wallet_id = null
    }
    
    const url = transferForm.value.id ? `/api/courier-transfers/${transferForm.value.id}` : '/api/courier-transfers'
    const body = { courier_transfer: transferForm.value }
    
    const response = transferForm.value.id 
      ? await apiPut(url, body)
      : await apiPost(url, body)
    
    if (response.ok) {
      showAddDialog.value = false
      loadTransfers()
      resetForm()
    } else {
      const errorData = await response.json()
      alert(t('courierTransfers.failedToSave', { errors: errorData.errors?.join(', ') || t('errors.unknownError') }))
    }
  } catch (error) {
    console.error('Save transfer error:', error)
    alert(t('courierTransfers.errorSaving'))
  }
}

const editTransfer = (transfer: CourierTransfer) => {
  // Extract IDs from nested objects if direct IDs are not available
  const fromWalletId = transfer.from_wallet_id || transfer.from_wallet?.id || null
  const fromClientId = transfer.from_client_id || transfer.from_client?.id || null
  const toWalletId = transfer.to_wallet_id || transfer.to_wallet?.id || null
  const toClientId = transfer.to_client_id || transfer.to_client?.id || null
  
  transferForm.value = {
    id: transfer.id,
    courier_id: transfer.courier?.id || null,
    coin_id: transfer.coin?.id || null,
    amount: transfer.amount || 0,
    status: transfer.status || 'received_from_wallets',
    from_wallet_id: fromWalletId,
    from_client_id: fromClientId,
    to_client_id: toClientId,
    to_wallet_id: toWalletId,
    description: transfer.description || '',
    transaction_date: transfer.transaction_date ? new Date(transfer.transaction_date).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16)
  }
  
  // Set source and destination types based on which ID is present
  sourceType.value = fromWalletId ? 'wallet' : 'client'
  destinationType.value = toWalletId ? 'wallet' : 'client'
  
  showAddDialog.value = true
}

const deleteTransfer = async (id: number) => {
  if (!confirm(t('courierTransfers.areYouSure'))) return
  try {
    const response = await apiDelete(`/api/courier-transfers/${id}`)
    if (response.ok) {
      loadTransfers()
    }
  } catch (error) {
    console.error('Delete transfer error:', error)
  }
}

const resetForm = () => {
  transferForm.value = {
    id: null,
    courier_id: null,
    coin_id: null,
    amount: 0,
    status: 'received_from_wallets',
    from_wallet_id: null,
    from_client_id: null,
    to_client_id: null,
    to_wallet_id: null,
    description: '',
    transaction_date: new Date().toISOString().slice(0, 16)
  }
  sourceType.value = 'wallet'
  destinationType.value = 'client'
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'received_from_wallets': return 'info'
    case 'in_a_way': return 'warning'
    case 'finished': return 'success'
    default: return 'default'
  }
}

const getStatusText = (status: string) => {
  switch (status) {
    case 'received_from_wallets': return t('courierTransfers.statusReceived')
    case 'in_a_way': return t('courierTransfers.statusInWay')
    case 'finished': return t('courierTransfers.statusFinished')
    default: return status
  }
}

const formatDate = (dateString: string) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString()
}

onMounted(() => {
  loadCoins()
  loadUsers()
  loadClients()
  loadWallets()
  loadTransfers()
})

// Reload when filters change
watch([filterStatus, filterCoin, filterCourier, filterDateFrom, filterDateTo], () => {
  loadTransfers()
})

const filteredTransfers = computed(() => {
  return transfers.value
})
</script>

<style scoped>
.amount-field-right :deep(input) {
  text-align: right !important;
}
</style>
